import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BuildIdentity, MergeNote } from "../components/DeviceIdentity";
import { api } from "../lib/api";
import { fmtAgo } from "../lib/format";
import { useDevicesLive, useLiveStore } from "../lib/ws";

function Copy({ text }: { text: string }) {
  const [ok, setOk] = useState(false);
  return (
    <span className="copy">
      {text}
      <button
        className="small"
        onClick={() => {
          navigator.clipboard?.writeText(text).then(() => {
            setOk(true);
            setTimeout(() => setOk(false), 1200);
          });
        }}
      >
        {ok ? "copied" : "copy"}
      </button>
    </span>
  );
}

/** Two-click inline confirm (no window.confirm — it blocks browser automation). */
function Revoke({ deviceId, onDone }: { deviceId: string; onDone: () => void }) {
  const [arm, setArm] = useState(false);
  const m = useMutation({ mutationFn: () => api.revokeDevice(deviceId), onSuccess: onDone });
  useEffect(() => {
    if (!arm) return;
    const t = setTimeout(() => setArm(false), 4000);
    return () => clearTimeout(t);
  }, [arm]);
  return (
    <button
      className={`small ${arm ? "danger" : ""}`}
      disabled={m.isPending}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (arm) m.mutate();
        else setArm(true);
      }}
      title="Invalidate this device's secret; it must scan a new QR to attach again"
    >
      {m.isPending ? "revoking…" : arm ? "click again to revoke" : "revoke"}
    </button>
  );
}

function Countdown({ until }: { until: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.floor((until - now) / 1000));
  const m = Math.floor(left / 60);
  const s = left % 60;
  return <span className={left === 0 ? "enroll-state revoked" : "muted"}>{left === 0 ? "code expired" : `expires in ${m}:${String(s).padStart(2, "0")}`}</span>;
}

export function Devices() {
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  // Each fetch mints a fresh single-use enrollment token; refetch = "new code".
  const info = useQuery({ queryKey: ["connect-info", url], queryFn: () => api.connectInfo(url ?? undefined), refetchInterval: 9 * 60_000, staleTime: 9 * 60_000 });
  const rest = useQuery({ queryKey: ["devices"], queryFn: api.devices, refetchInterval: 15_000 });
  const live = useDevicesLive();
  const runStatus = useLiveStore((s) => s.runStatus);
  const devices = useMemo(() => {
    const m: Record<string, any> = {};
    for (const d of rest.data ?? []) m[d.deviceId] = d;
    for (const [id, d] of Object.entries(live)) m[id] = { ...m[id], ...d };
    return Object.values(m).sort((a, b) => Number(b.online) - Number(a.online) || (b.lastSeen ?? 0) - (a.lastSeen ?? 0));
  }, [rest.data, live]);
  const primary = url ?? info.data?.primary;

  return (
    <div className="stack">
      <div className="split">
        <div className="panel">
          <h2>Devices</h2>
          {devices.length === 0 && <div className="empty">No device has attached yet. Scan the QR from the facade's settings, or set the URL over adb.</div>}
          <div className="grid cards">
            {devices.map((d) => {
              const rs = runStatus[d.deviceId];
              const running = d.currentRunId ?? (rs?.state === "running" ? rs.runId : null);
              return (
                <Link key={d.deviceId} to={`/d/${encodeURIComponent(d.deviceId)}`} className="panel card">
                  <div className="title">
                    <span className={`dot ${d.online ? "online" : ""}`} />
                    {d.nickname || d.deviceId}
                  </div>
                  <dl className="kv" style={{ marginTop: 6 }}>
                    <dt>id</dt>
                    <dd className="mono">
                      {d.deviceId}
                      <MergeNote device={d} />
                    </dd>
                    <dt>model</dt>
                    <dd>
                      {d.info?.device?.manufacturer} {d.info?.device?.model} {d.info?.device?.sdk ? `· sdk ${d.info.device.sdk}` : ""}
                    </dd>
                    <dt>build</dt>
                    <dd>
                      <BuildIdentity device={d} />
                    </dd>
                    <dt>seen</dt>
                    <dd>{d.online ? "online" : fmtAgo(d.lastSeen)}</dd>
                    <dt>auth</dt>
                    <dd className="row" style={{ gap: 8, alignItems: "center" }}>
                      {d.enrolled ? (
                        <span className="enroll-state enrolled">enrolled {d.enrolledTs ? fmtAgo(d.enrolledTs) : ""}</span>
                      ) : d.revokedTs ? (
                        <span className="enroll-state revoked">revoked {fmtAgo(d.revokedTs)}</span>
                      ) : (
                        <span className="enroll-state none">{info.data?.authDisabled ? "not enrolled (auth off)" : "not enrolled"}</span>
                      )}
                      {d.enrolled && <Revoke deviceId={d.deviceId} onDone={() => qc.invalidateQueries({ queryKey: ["devices"] })} />}
                    </dd>
                    <dt>run</dt>
                    <dd>
                      {running ? (
                        <span className="badge running">running · {rs?.prompt ? JSON.stringify(rs.prompt).slice(0, 40) : running.slice(0, 12)}</span>
                      ) : (
                        <span className="muted">idle</span>
                      )}
                    </dd>
                  </dl>
                </Link>
              );
            })}
          </div>
        </div>
        <div className="panel">
          <h2>Attach a device</h2>
          {info.data ? (
            <div className="stack">
              <div className="qr" dangerouslySetInnerHTML={{ __html: info.data.qrSvg }}></div>
              <div className="row" style={{ alignItems: "center", gap: 10 }}>
                <Countdown until={info.data.expiresAt} />
                <button className="small" onClick={() => qc.invalidateQueries({ queryKey: ["connect-info"] })} disabled={info.isFetching}>
                  {info.isFetching ? "…" : "new code"}
                </button>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                The QR carries the server URL and a single-use enrollment code (10 min). The app exchanges it for its own device secret; the
                secret never appears here.
                {info.data.authDisabled && " Auth is off on this server: unenrolled devices are accepted too."}
              </div>
              <div className="stack">
                {info.data.wsUrls.map((u) => (
                  <div key={u} className="row">
                    <input type="radio" name="url" checked={primary === u} onChange={() => setUrl(u)} />
                    <Copy text={u} />
                  </div>
                ))}
                <div className="row">
                  <input type="radio" name="url" checked={primary === info.data.emulatorUrl} onChange={() => setUrl(info.data!.emulatorUrl)} />
                  <Copy text={info.data.emulatorUrl} /> <span className="muted">emulator</span>
                </div>
              </div>
              <details>
                <summary className="muted">via adb</summary>
                <pre className="mono" style={{ marginTop: 6 }}>{`adb shell am broadcast -a com.facade.controller.SET_DASHBOARD --es url ${primary} --es token ${info.data.token}`}</pre>
                <pre className="mono" style={{ marginTop: 6 }}>{`# enroll a fake device, then connect with its secret
curl -s -X POST ${location.origin}/api/devices/enroll -H 'content-type: application/json' \\
  -d '{"token":"${info.data.token}","deviceId":"fake-1","nickname":"Fake Pixel"}'
npm run fake-device -- --url ${primary} --secret <deviceSecret>`}</pre>
              </details>
              <div className="banner">Dev/inspection opt-in: prompts, model responses, screenshots and node trees leave the device to this server.</div>
            </div>
          ) : (
            <div className="muted">loading…</div>
          )}
        </div>
      </div>
    </div>
  );
}
